/**
 * HPQ Quick Cart v2 — Fully local cart drawer
 *
 * Zero server calls in the render path.
 *   - Items, prices, subtotal, shipping bar: from hpqCart
 *   - Cross-sells: from embedded catalog JSON (Liquid-rendered on page load)
 *   - Tier discounts: server-reconciled prices arrive via hpq:cart-synced
 *
 * Server only touched for: background sync (fire-and-forget) + checkout gate.
 *
 * ES5+. No arrow functions, no const/let.
 */
(function () {
  'use strict';

  var drawer = null;
  var wrapper = null;
  var isOpen = false;
  var noteTimer = null;
  var lastRenderVersion = -1;
  var THRESHOLD = (window.hpqFreeShip && window.hpqFreeShip.threshold) || 5900;

  // Free-ship state (threshold + coupon-adjusted effective total + status + the "checking…"
  // pending flag) is owned by hpq-free-ship.js and shared with the cart-page bar + nav bar, so
  // every shipping claim in the drawer agrees with them, the coupon summary, and checkout.
  // Fall back to a local compute only if that module somehow failed to load.
  function shipState(totalCents, count) {
    var fs = window.hpqFreeShip;
    if (fs) return fs.stateFor(totalCents, count);
    var eff = totalCents;
    return {
      threshold: THRESHOLD, effectiveCents: eff, remainingCents: Math.max(0, THRESHOLD - eff),
      pct: Math.min(100, Math.round((eff / THRESHOLD) * 100)), pending: false,
      status: eff >= THRESHOLD ? 'free' : (count > 0 ? 'flat' : 'empty')
    };
  }
  var cartVersion = 0;
  var headerCache = '';
  var catalogData = null;  // embedded product catalog for cross-sells
  var tierRules = null;    // from #hpq-tier-rules: { "Hot Sauce": { min: 3, off: 200, label: "sauces" }, ... }

  // Branded checkmark SVG (free-shipping unlocked cue). Single source of truth
  // for this file — DOM-injected via innerHTML, which remounts the element on
  // every write, triggering the .hpq-check-icon scale-in animation in CSS.
  var HPQ_CHECK_SVG = '<svg class="hpq-check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#00953B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ── Init ───────────────────────────────────────────────────────────

  function init() {
    drawer = document.getElementById('hpq-quick-cart');
    if (!drawer) return;

    wrapper = drawer.querySelector('.quick-cart__wrapper');

    // Cache the Liquid-rendered header (close button SVG)
    var header = drawer.querySelector('.quick-cart__header');
    if (header) headerCache = header.outerHTML;

    // Load embedded data
    var catEl = document.getElementById('hpq-catalog-data');
    if (catEl) { try { catalogData = JSON.parse(catEl.textContent); } catch (e) {} }
    var tierEl = document.getElementById('hpq-tier-rules');
    if (tierEl) { try { tierRules = JSON.parse(tierEl.textContent); } catch (e) {} }

    drawer.addEventListener('click', handleClick);

    // Cart icon interception (capture phase)
    document.addEventListener('click', function (e) {
      var icon = e.target.closest('[data-js-cart-icon]');
      if (!icon) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close();
    });

    // Track changes for skip-if-unchanged
    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.source !== 'hpq-quick-cart') cartVersion++;
    });

    // After sync: surgical price update (server may have applied discounts)
    document.addEventListener('hpq:cart-synced', function () {
      cartVersion++;
      if (isOpen) reconcilePrices();
    });

    // Shared cart ViewModel changed (cart change, coupon quote landed/cleared, or a quote went
    // in flight → "Checking…"). Surgically refresh the drawer footer + bar from it. Lightweight
    // (no full re-render → no cart:updated loop); no-ops safely when the drawer isn't rendered.
    document.addEventListener('hpq:cart-changed', function () { updateFooter(); });
  }

  // ── Open / Close ───────────────────────────────────────────────────

  function open() {
    if (!wrapper || isOpen) return;
    isOpen = true;

    if (cartVersion !== lastRenderVersion) {
      renderFromLocal();
      lastRenderVersion = cartVersion;
    }

    wrapper.classList.add('active');
    document.body.setAttribute('data-fluorescent-overlay-open', 'true');
    document.body.style.overflow = 'hidden';

    var animRoot = drawer.closest('.animation--quick-cart') || drawer;
    animRoot.classList.add('animation--quick-cart-revealed');

    requestAnimationFrame(adjustPadding);

    if (window.hpqCart.isDirty()) window.hpqCart.syncNow();

    document.dispatchEvent(new CustomEvent('quick-cart:open', {
      detail: { cart: window.hpqCart ? window.hpqCart.getCart() : {} }
    }));
  }

  function close() {
    if (!wrapper || !isOpen) return;
    isOpen = false;
    wrapper.classList.remove('active');
    setTimeout(function () {
      document.body.setAttribute('data-fluorescent-overlay-open', 'false');
      document.body.style.overflow = '';
      var animRoot = drawer.closest('.animation--quick-cart') || drawer;
      animRoot.classList.remove('animation--quick-cart-revealed');
    }, 500);
    document.dispatchEvent(new CustomEvent('quick-cart:close'));
  }

  function adjustPadding() {
    var items = drawer.querySelector('.quick-cart__items');
    var form = drawer.querySelector('.quick-cart__form');
    var footer = drawer.querySelector('.quick-cart__footer');
    if (!footer) return;
    if (items) items.style.paddingBottom = footer.clientHeight + 'px';
    if (form) form.style.paddingBottom = '0px';
  }

  // ── Render from hpqCart (0ms, no network) ──────────────────────────

  function renderFromLocal() {
    if (!window.hpqCart) return;
    var container = drawer.querySelector('.quick-cart__container');
    if (!container) return;

    var items = window.hpqCart.getItems();
    var cart = window.hpqCart.getCart();

    // Enrich items with catalog metadata (fills images for homepage-added items)
    for (var i = 0; i < items.length; i++) enrichFromCatalog(items[i]);

    // Header with count badge
    var hdr = buildHeaderHtml(cart.item_count);

    if (items.length === 0) {
      // Empty state — Auntie Fu voice
      var emptyHtml = '<div class="hpq-cart-empty">' +
        '<p class="hpq-cart-empty__message">Nothing here yet.<br>Auntie suggests starting with the chili crisp.</p>' +
        '<a href="/collections/sauces" class="hpq-cart-empty__cta btn btn--primary">Browse Sauces</a>' +
        '</div>';
      container.innerHTML = hdr + emptyHtml;
      return;
    }

    var itemsHtml = '';
    for (var j = 0; j < items.length; j++) itemsHtml += buildItemHtml(items[j], items);

    var crossSellsHtml = buildCrossSellsHtml(items);
    var shippingHtml = buildShippingBarHtml(shipState(cart.total_price, cart.item_count));
    var footerHtml = buildFooterHtml(cart);

    container.innerHTML =
      '<div class="hpq-cart-header-group">' + hdr + shippingHtml + '</div>' +
      '<div class="quick-cart__form" data-scroll-lock-ignore>' +
        '<div class="quick-cart__items animation--quick-cart-items" data-hpq-cart-items>' +
          itemsHtml + crossSellsHtml +
        '</div>' +
        footerHtml +
      '</div>';

    // The drawer was just JS-rebuilt with a fresh coupon box — let hpq-cart-coupon.js bind +
    // reveal + quote it, and the shipping bars re-run. source:'hpq-quick-cart' so our own
    // cart:updated listener (which bumps cartVersion) ignores it and we don't re-render-loop.
    dispatchOptimisticCart();
  }

  // Publish a COMPLETE optimistic cart (subtotal + tier-adjusted total + savings, all from the
  // local cart + tier calculator) so the shared store recomputes correct totals. hpqCart.setQty
  // fires its own cart:updated first, but that payload carries no original_total_price and the
  // stale server total_price — the store would show Subtotal $0.00 / a wrong Total until sync.
  // Dispatching this right after every add/remove/qty mutation makes the store's VM correct
  // before the rAF-batched totals render flushes (last-queued VM wins → no flash).
  function dispatchOptimisticCart() {
    if (!window.hpqCart) return;
    var items = window.hpqCart.getItems();
    // getItems() returns fresh, UN-enriched objects; getDisplayPrice() needs item._type (set by
    // enrichFromCatalog) to apply tier/volume pricing. Without this, optimistic totals collapse to
    // base prices — the tier discount silently vanishes on every qty click.
    for (var i = 0; i < items.length; i++) enrichFromCatalog(items[i]);
    var cart = window.hpqCart.getCart();
    var optimisticCart = buildOptimisticCart(items, cart.item_count, cart.currency || 'USD', getDisplayPrice);
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { source: 'hpq-quick-cart', cart: optimisticCart }
    }));
  }

  // ── HTML Builders ─────────────────────────────────────────────────

  function buildHeaderHtml(count) {
    if (count > 0 && headerCache.indexOf('data-hpq-cart-count') !== -1) {
      return headerCache.replace(/<sup[^>]*data-hpq-cart-count[^>]*>.*?<\/sup>/, '<sup data-hpq-cart-count>' + count + '</sup>');
    }
    if (count > 0) {
      return headerCache.replace(/(<\/h2>)/, '<sup data-hpq-cart-count>' + count + '</sup>$1');
    }
    return headerCache.replace(/<sup[^>]*>.*?<\/sup>/g, '');
  }

  function buildItemHtml(item, cartItems) {
    var imgSrc = item.image || '';
    if (imgSrc) imgSrc += (imgSrc.indexOf('?') !== -1 ? '&' : '?') + 'width=80';

    // Compute effective price (tier discount applied locally, 0ms)
    var displayPrice = getDisplayPrice(item, cartItems);
    var isTier = displayPrice < item.price;
    var priceHtml = isTier
      ? '<s class="hpq-cart-item__price" style="opacity:0.5">' + formatMoney(item.price) + '</s> ' +
        '<span class="hpq-cart-item__price sale-price" data-hpq-item-price>' + formatMoney(displayPrice) + '</span>'
      : '<span class="hpq-cart-item__price" data-hpq-item-price>' + formatMoney(displayPrice) + '</span>';

    // Stepper: minus button for all quantities (qty=0 triggers removeItem via changeQty)
    var minusSvg = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    var minusBtn = '<button type="button" class="hpq-cart-stepper__btn" data-hpq-qty-minus aria-label="Decrease">' + minusSvg + '</button>';

    return '<div class="hpq-cart-item" data-hpq-item data-variant-id="' + esc(String(item.variant_id)) + '" data-orig-price="' + item.price + '" data-unit-price="' + displayPrice + '">' +
      '<a href="' + esc(item.url || '#') + '" class="hpq-cart-item__image">' +
        (imgSrc ? '<img src="' + esc(imgSrc) + '" alt="" width="80" height="80">' : '') +
      '</a>' +
      '<div class="hpq-cart-item__details">' +
        '<h4 class="hpq-cart-item__title"><a href="' + esc(item.url || '#') + '">' + esc(item.title) + '</a></h4>' +
        (item.variant_title ? '<p class="quick-cart__item-variant fs-body-60 t-opacity-60">' + esc(item.variant_title) + '</p>' : '') +
        '<div class="hpq-cart-item__price">' + priceHtml + '</div>' +
        '<div class="hpq-cart-stepper" data-hpq-qty-wrap>' +
          minusBtn +
          '<input type="number" value="' + item.quantity + '" min="0" class="hpq-cart-stepper__qty" data-hpq-qty-input readonly>' +
          '<button type="button" class="hpq-cart-stepper__btn" data-hpq-qty-plus aria-label="Increase"><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2.5v7M2.5 6h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function buildOptimisticCart(items, itemCount, currency, getDisplayPrice) {
    items = items || [];
    var originalTotal = 0;
    var adjustedTotal = 0;
    var calc = typeof getDisplayPrice === 'function' ? getDisplayPrice : null;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var qty = parseInt(item.quantity, 10) || 0;
      var originalPrice = parseInt(item.price, 10) || 0;
      var adjustedPrice = calc ? parseInt(calc(item, items), 10) : originalPrice;
      if (!isFinite(adjustedPrice)) adjustedPrice = originalPrice;
      if (adjustedPrice < 0) adjustedPrice = 0;

      originalTotal += originalPrice * qty;
      adjustedTotal += adjustedPrice * qty;
    }

    return {
      item_count: itemCount,
      original_total_price: originalTotal,
      total_price: adjustedTotal,
      total_discount: originalTotal - adjustedTotal,
      currency: currency || 'USD'
    };
  }

  function buildShippingBarHtml(state) {
    var pct = state.pct, msg;
    if (state.status === 'pending') {
      msg = 'Checking shipping…'; // coupon quote in flight — no stale claim to flash
    } else if (state.status === 'free') {
      pct = 100;
      msg = HPQ_CHECK_SVG + ' You qualify for <strong>free shipping!</strong>';
    } else if (state.status === 'empty') {
      pct = 0;
      msg = 'Free shipping on orders over <strong>' + formatMoney(state.threshold) + '</strong>';
    } else {
      msg = 'You\'re <strong>' + formatMoney(state.remainingCents) + '</strong> away from free shipping';
    }
    return '<div class="free-shipping-bar free-shipping-bar--loaded" data-free-shipping-bar data-threshold="' + (state.threshold / 100) + '">' +
      '<div class="inventory-counter__message-wrapper ff-body fs-body-75"><h4 class="free-shipping-bar__message">' + msg + '</h4></div>' +
      '<div class="free-shipping-bar__bar"><span class="free-shipping-bar__bar-progress" style="width:' + pct + '%"></span></div>' +
    '</div>';
  }

  function buildFooterHtml(cart) {
    // Totals (Subtotal/Savings/Total) are rendered into [data-hpq-totals-root] by
    // hpq-cart-totals.js from the shared view-model; the drawer only supplies the container.

    // Unified coupon box — this drawer is fully JS-rendered, so the box must live in the JS
    // template (a Liquid include here would be wiped by renderFromLocal). Matches the shell in
    // snippets/hpq-cart-coupon.liquid; hpq-cart-coupon.js reveals + populates it. NOT a <form>
    // (nested in the checkout form would be stripped) — the JS binds click + Enter.
    var couponBoxHtml =
      '<div class="hpq-cart-coupon ff-body" data-hpq-coupon-box data-hpq-coupon-form hidden>' +
        '<label class="hpq-cart-coupon__label ff-body fs-body-100" for="hpq-coupon-input-drawer">Promo code</label>' +
        '<div class="hpq-cart-coupon__row">' +
          '<input class="hpq-cart-coupon__input input" id="hpq-coupon-input-drawer" type="text" ' +
            'autocomplete="off" autocapitalize="characters" autocorrect="off" spellcheck="false" ' +
            'placeholder="Enter code" data-hpq-coupon-input>' +
          '<button class="hpq-cart-coupon__apply" type="button" data-hpq-coupon-apply>Apply</button>' +
        '</div>' +
        '<p class="hpq-cart-coupon__msg fs-body-75" data-hpq-coupon-msg role="alert" hidden></p>' +
        '<ul class="hpq-cart-coupon__chips" data-hpq-coupon-chips></ul>' +
        '<div class="hpq-cart-coupon__summary" data-hpq-coupon-summary hidden></div>' +
      '</div>';

    return '<div class="quick-cart__footer animation--quick-cart-footer"><div class="quick-cart__footer-inner">' +
      couponBoxHtml +
      '<div class="quick-cart__footer-totals" data-hpq-totals-root></div>' +
      '<form action="/cart" method="post"><button type="submit" name="checkout" class="quick-cart__submit btn btn--full btn--primary">Checkout</button></form>' +
    '</div></div>';
  }

  // ── Cross-sells (local, from embedded catalog) ────────────────────

  function buildCrossSellsHtml(cartItems) {
    if (!catalogData) return '';

    // Determine cart composition
    var cartVids = {};
    var cartTypes = {};
    for (var i = 0; i < cartItems.length; i++) {
      cartVids[String(cartItems[i].variant_id)] = true;
      // Look up type from catalog
      for (var c = 0; c < catalogData.length; c++) {
        if (String(catalogData[c].vid) === String(cartItems[i].variant_id)) {
          cartTypes[catalogData[c].type] = true;
          break;
        }
      }
    }

    // If all categories covered, no cross-sells
    if (cartTypes['Hot Sauce'] && cartTypes['Noodles'] && cartTypes['Soup Base']) return '';

    // Pick suggestions from missing categories
    var suggestions = [];
    for (var j = 0; j < catalogData.length; j++) {
      var p = catalogData[j];
      if (cartVids[String(p.vid)]) continue;
      if (cartTypes[p.type]) continue;
      suggestions.push(p);
      if (suggestions.length >= 3) break;
    }
    if (suggestions.length === 0) return '';

    // Heading
    var missing = (!cartTypes['Hot Sauce'] ? 1 : 0) + (!cartTypes['Noodles'] ? 1 : 0) + (!cartTypes['Soup Base'] ? 1 : 0);
    var heading;
    if (missing >= 3) heading = 'Build your hotpot night';
    else if (missing === 2) heading = 'Complete your meal';
    else if (!cartTypes['Hot Sauce']) heading = 'Sauce is the soul of hotpot';
    else if (!cartTypes['Noodles']) heading = 'Every hotpot needs noodles';
    else heading = 'Start with the base';

    var html = '<div class="hpq-cross-sells"><div class="hpq-cross-sells__header">' +
      '<h4 class="hpq-cross-sells__heading ff-heading fs-heading-6-base">' + esc(heading) + '</h4></div>' +
      '<div class="hpq-cross-sells__items">';

    for (var k = 0; k < suggestions.length; k++) {
      var s = suggestions[k];
      var sImg = s.image ? '<img src="' + esc(s.image) + '" alt="" width="56" height="56" style="width:100%;height:100%;object-fit:cover">' : '';
      html += '<div class="hpq-cross-sells__card">' +
        '<a href="' + esc(s.url) + '" class="hpq-cross-sells__image">' + sImg + '</a>' +
        '<div class="hpq-cross-sells__info"><span class="hpq-cross-sells__title ff-body fs-body-75">' + esc(s.title) + '</span>' +
        '<span class="hpq-cross-sells__price ff-body fs-body-75 t-subdued">' + formatMoney(s.price) + '</span></div>' +
        '<button type="button" class="hpq-cross-sells__add btn btn--tertiary btn--x-small" data-add-item-id="' + s.vid + '" data-unit-price="' + s.price + '" data-product-url="' + esc(s.url) + '">+ Add</button>' +
      '</div>';
    }
    return html + '</div></div>';
  }

  // ── Catalog enrichment + tier pricing ───────────────────────────

  function enrichFromCatalog(item) {
    if (!catalogData) return;
    for (var i = 0; i < catalogData.length; i++) {
      if (String(catalogData[i].vid) === String(item.variant_id)) {
        if (!item.image) item.image = catalogData[i].image || '';
        if (!item.url) item.url = catalogData[i].url || '';
        item._type = catalogData[i].type; // Store product type for tier calc
        return;
      }
    }
  }

  // Count items of a given product type in the cart
  function countByType(type, cartItems) {
    var count = 0;
    for (var i = 0; i < cartItems.length; i++) {
      if (cartItems[i]._type === type) count += cartItems[i].quantity;
    }
    return count;
  }

  // Effective display price (tier or regular).
  //
  // The rule is a per-unit DELTA, not a destination price — Shopify's discounts are
  // fixed_amount / allocation_method "each" (measured 2026-08-23). Subtracting keeps this
  // correct if a product in the category is ever priced differently from its siblings;
  // a stored tier price would silently misquote it. Never reintroduce rule.price.

  // Resolve a product_type to its tier rule. Classifies the way Liquid does — exact key
  // first, then the rule's own `match` substrings — so a product typed 'Chili Sauce'
  // resolves here exactly as it does server-side. Keying on the exact product_type alone
  // was the second half of the duplicated category map.
  function ruleForType(type) {
    if (!tierRules || !type) return null;
    var exact = tierRules[type];
    if (exact && typeof exact.off === 'number') return exact;
    var lower = String(type).toLowerCase();
    for (var key in tierRules) {
      if (!Object.prototype.hasOwnProperty.call(tierRules, key)) continue;
      var r = tierRules[key];
      if (!r || typeof r.off !== 'number' || !r.match) continue;
      for (var i = 0; i < r.match.length; i++) {
        if (lower.indexOf(String(r.match[i]).toLowerCase()) !== -1) return r;
      }
    }
    return null;
  }

  function getDisplayPrice(item, cartItems) {
    var rule = ruleForType(item._type);
    if (!rule) return item.price;
    var typeCount = countByType(item._type, cartItems);
    if (typeCount < rule.min) return item.price;
    return Math.max(0, item.price - rule.off);
  }

  // ── Click delegation ──────────────────────────────────────────────

  function handleClick(e) {
    if (e.target.closest('[data-hpq-cart-close]')) { close(); return; }

    var minus = e.target.closest('[data-hpq-qty-minus]');
    if (minus) { var mi = minus.closest('[data-hpq-item]'); if (mi) changeQty(mi, -1); return; }

    var plus = e.target.closest('[data-hpq-qty-plus]');
    if (plus) { var pi = plus.closest('[data-hpq-item]'); if (pi) changeQty(pi, 1); return; }

    if (e.target.closest('[data-hpq-remove]')) { var ri = e.target.closest('[data-hpq-item]'); if (ri) removeItem(ri); return; }

    var addBtn = e.target.closest('[data-add-item-id]');
    if (addBtn) { addCrossSellItem(addBtn); return; }
  }

  // ── Quantity change (0ms) ─────────────────────────────────────────

  function changeQty(itemEl, delta) {
    var vid = itemEl.getAttribute('data-variant-id');
    var input = itemEl.querySelector('[data-hpq-qty-input]');
    var currentQty = parseInt(input.value, 10) || 0;
    var newQty = Math.max(0, currentQty + delta);

    if (newQty === 0) { removeItem(itemEl); return; }

    // Read the ORIGINAL (non-tier) price for hpqCart storage
    var origPrice = parseInt(itemEl.getAttribute('data-orig-price') || itemEl.getAttribute('data-unit-price'), 10) || 0;

    input.value = newQty;
    window.hpqCart.setQty(vid, newQty, origPrice);

    // Qty change may cross tier threshold — re-render all prices
    updateItemPrices();
    // Feed the store a complete optimistic cart so Subtotal/Total update on this qty click
    // (before updateFooter reads the fresh VM for the shipping bar).
    dispatchOptimisticCart();
    updateFooter();
  }

  // Re-compute all item prices (tier thresholds may have changed)
  function updateItemPrices() {
    var items = window.hpqCart.getItems();
    for (var i = 0; i < items.length; i++) enrichFromCatalog(items[i]);

    var itemEls = drawer.querySelectorAll('[data-hpq-item]');
    for (var j = 0; j < itemEls.length; j++) {
      var vid = itemEls[j].getAttribute('data-variant-id');
      for (var k = 0; k < items.length; k++) {
        if (String(items[k].variant_id) === vid) {
          var dp = getDisplayPrice(items[k], items);
          var isTier = dp < items[k].price;
          var pw = itemEls[j].querySelector('.hpq-cart-item__price');
          if (pw) {
            pw.innerHTML = isTier
              ? '<s class="hpq-cart-item__price" style="opacity:0.5">' + formatMoney(items[k].price) + '</s> ' +
                '<span class="hpq-cart-item__price sale-price" data-hpq-item-price>' + formatMoney(dp) + '</span>'
              : '<span class="hpq-cart-item__price" data-hpq-item-price>' + formatMoney(dp) + '</span>';
          }
          itemEls[j].setAttribute('data-orig-price', items[k].price);
          itemEls[j].setAttribute('data-unit-price', dp);
          break;
        }
      }
    }
  }

  // ── Remove item (0ms + 200ms animation) ───────────────────────────

  function removeItem(itemEl) {
    var vid = itemEl.getAttribute('data-variant-id');
    itemEl.style.transition = 'opacity 0.15s, transform 0.15s';
    itemEl.style.opacity = '0';
    itemEl.style.transform = 'translateX(20px)';
    window.hpqCart.setQty(vid, 0);
    // Republish complete optimistic totals NOW — the item is already gone from the local cart, so
    // the store must not keep showing the pre-removal total through the 150ms fade animation.
    dispatchOptimisticCart();

    setTimeout(function () {
      if (itemEl.parentNode) itemEl.parentNode.removeChild(itemEl);
      if (window.hpqCart.getItemCount() === 0) {
        cartVersion++;
        lastRenderVersion = -1; // Force re-render on next open
        renderFromLocal();
      } else {
        updateFooter();
        updateCrossSells();
      }
    }, 150);
  }

  // ── Footer (subtotal, count, shipping bar) ────────────────────────

  function updateFooter() {
    if (!drawer || !window.hpqCart) return; // fires on every hpq:cart-changed — guard hard
    var cart = window.hpqCart.getCart();

    var cnt = drawer.querySelector('[data-hpq-cart-count]');
    if (cnt) cnt.textContent = cart.item_count;

    // The drawer's top free-shipping progress bar reads vm.freeShip from the single shared store
    // (hpqCartState) — no independent re-derivation — so it can never disagree with the totals
    // renderer. Fall back to the legacy shipState() (window.hpqFreeShip) only if the shared store
    // somehow hasn't initialized yet.
    var vm = window.hpqCartState && typeof window.hpqCartState.getVM === 'function' ? window.hpqCartState.getVM() : null;
    var state = vm ? vm.freeShip : shipState(cart.total_price, cart.item_count);

    var bar = drawer.querySelector('[data-free-shipping-bar]');
    if (bar) {
      var progress = bar.querySelector('.free-shipping-bar__bar-progress');
      var message = bar.querySelector('.free-shipping-bar__message');
      if (progress && message) {
        if (state.status === 'pending') {
          // Coupon quote in flight — hold the fill, show a neutral note, don't flash a claim.
          message.innerHTML = 'Checking shipping…';
        } else if (state.status === 'free') {
          progress.style.width = '100%';
          message.innerHTML = HPQ_CHECK_SVG + ' You qualify for <strong>free shipping!</strong>';
        } else if (state.status === 'empty') {
          progress.style.width = '0%';
          message.innerHTML = 'Free shipping on orders over <strong>' + formatMoney(state.threshold) + '</strong>';
        } else {
          progress.style.width = state.pct + '%';
          message.innerHTML = 'You\'re <strong>' + formatMoney(state.remainingCents) + '</strong> away from free shipping';
        }
      }
    }

    requestAnimationFrame(adjustPadding);
  }

  // ── Cross-sells update (after item add/remove) ────────────────────

  function updateCrossSells() {
    var itemsArea = drawer.querySelector('[data-hpq-cart-items]');
    if (!itemsArea) return;

    // Remove old
    var old = itemsArea.querySelector('.hpq-cross-sells');
    if (old) old.parentNode.removeChild(old);

    // Build new
    var html = buildCrossSellsHtml(window.hpqCart.getItems());
    if (html) itemsArea.insertAdjacentHTML('beforeend', html);
    requestAnimationFrame(adjustPadding);
  }

  // ── Post-sync reconciliation (surgical, no full re-render) ────────

  function reconcilePrices() {
    // After sync, server may have applied/removed discounts.
    // hydrateFromShopify already updated hpqCart prices, so rebuild local tier
    // presentation from the refreshed cart state instead of overwriting it.
    var cartItems = window.hpqCart.getItems();
    for (var i = 0; i < cartItems.length; i++) enrichFromCatalog(cartItems[i]);

    var itemEls = drawer.querySelectorAll('[data-hpq-item]');
    for (var j = 0; j < itemEls.length; j++) {
      var vid = itemEls[j].getAttribute('data-variant-id');
      for (var k = 0; k < cartItems.length; k++) {
        if (String(cartItems[k].variant_id) === vid) {
          itemEls[j].setAttribute('data-orig-price', cartItems[k].price);

          // Update qty if server corrected it (e.g., inventory limit)
          var input = itemEls[j].querySelector('[data-hpq-qty-input]');
          if (input && parseInt(input.value, 10) !== cartItems[k].quantity) {
            input.value = cartItems[k].quantity;
          }
          break;
        }
      }
    }

    updateItemPrices();
    updateFooter();
    updateCrossSells();
  }

  // ── Cross-sell add ────────────────────────────────────────────────

  function addCrossSellItem(button) {
    var vid = button.getAttribute('data-add-item-id');
    if (!vid) return;

    var priceCents = parseInt(button.getAttribute('data-unit-price'), 10) || 0;
    var card = button.closest('.hpq-cross-sells__card');
    var titleEl = card && card.querySelector('.hpq-cross-sells__title');
    var imgEl = card && card.querySelector('.hpq-cross-sells__image img');
    var title = titleEl ? titleEl.textContent.trim() : '';
    var meta = {
      image: imgEl ? imgEl.src : '',
      url: button.getAttribute('data-product-url') || ''
    };

    window.hpqCart.addQty(vid, 1, priceCents, title, meta);

    // Full re-render: new item in cart, cross-sells recalculated
    cartVersion++;
    lastRenderVersion = -1;
    renderFromLocal();
    requestAnimationFrame(adjustPadding);
  }

  // ── Helpers ────────────────────────────────────────────────────────

  function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }

  function esc(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  window.__hpqTestBuildOptimisticCart = buildOptimisticCart;

  // ── Boot ───────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
